import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getConfig } from '../lib/config';
import { useSiteSettings } from '../lib/siteSettings';
import * as registrationApi from '../api/registration';
import { useToast } from '../components/toast';
import { ErrorAlert, Field } from '../components/ui';
import { ui, cn } from '../lib/ui';

const PASSWORD_HINT =
  'At least 12 characters with uppercase, lowercase, number, and symbol.';

export default function Register() {
  const { register } = useAuth();
  const { productName } = getConfig();
  const { settings } = useSiteSettings();
  const toast = useToast();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    investorType: 'non_accredited',
    company: ''
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register({ ...form, company: form.company || undefined });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setResendBusy(true);
    try {
      await registrationApi.resendVerification(form.email);
      toast.success('Verification email sent.');
    } catch (err) {
      toast.error(err);
    } finally {
      setResendBusy(false);
    }
  }

  // FEA-30: registration can be closed site-wide (docs/FRONTEND-PLAN.md
  // §4.3) — checked only before a fresh submission; a signup already in
  // flight still gets its "check your email" confirmation below.
  if (!done && settings.registrationOpen === false) {
    return (
      <div className={ui.authScreen}>
        <div className={ui.authCard}>
          <div className={ui.authBrand}>
            <h1 className={ui.h1}>{productName}</h1>
          </div>
          <h2 className={ui.h2}>Registration is currently closed</h2>
          <p className={ui.muted}>
            New member registration is temporarily closed. Please check back later.
          </p>
          {settings.supportEmail && (
            <p className="text-body-s">
              Questions? Contact{' '}
              <a href={`mailto:${settings.supportEmail}`}>{settings.supportEmail}</a>.
            </p>
          )}
          <p className="text-body-s mt-4">
            Already registered? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className={ui.authScreen}>
        <div className={ui.authCard}>
          <h1 className={ui.h1}>Check your email</h1>
          <p>
            Your account was created. We sent a verification link to{' '}
            <strong>{form.email}</strong> — verify your email, then sign in.
          </p>
          <button
            className={cn(ui.btnSecondary, 'w-full')}
            type="button"
            onClick={onResend}
            disabled={resendBusy}
          >
            {resendBusy ? 'Sending…' : 'Resend verification email'}
          </button>
          <Link className={cn(ui.btnPrimary, 'mt-4 w-full')} to="/login">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.authScreen}>
      <div className={ui.authCard}>
        <div className={ui.authBrand}>
          <h1 className={ui.h1}>{productName}</h1>
          <p className={cn(ui.muted, 'text-body-s')}>Create your investor account</p>
        </div>
        <ErrorAlert error={error} />
        <form onSubmit={onSubmit}>
          <div className={cn(ui.grid, ui.gridMd2)}>
            <Field label="First name" required value={form.firstName} onChange={set('firstName')} />
            <Field label="Last name" required value={form.lastName} onChange={set('lastName')} />
          </div>
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={set('email')}
          />
          <Field
            label="Username"
            autoComplete="username"
            required
            value={form.username}
            onChange={set('username')}
          />
          <Field
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            hint={PASSWORD_HINT}
            value={form.password}
            onChange={set('password')}
          />
          <Field label="Company (optional)" value={form.company} onChange={set('company')} />
          <label className={ui.field}>
            <span className={ui.label}>Investor type (self-declared)</span>
            <select className={ui.input} value={form.investorType} onChange={set('investorType')}>
              <option value="non_accredited">Non-accredited investor</option>
              <option value="accredited">Accredited investor</option>
            </select>
            <span className={ui.hint}>
              Declaring accredited status does not grant access by itself —
              accredited features unlock after a proof-of-funds review.
            </span>
          </label>
          <button className={cn(ui.btnPrimary, 'w-full')} type="submit" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <p className="text-body-s mt-4">
          Already registered? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
